import type { ReactNode } from 'react';
import { Icon, type IconName } from './Icon';
import { Avatar } from './Avatar';
import { useT } from '../../i18n';
import type { Baby } from '../../data/types';

export type TabId = 'today' | 'activities' | 'list' | 'timeline' | 'leaps' | 'growth' | 'journey' | 'ai';

interface AppShellProps {
  tab: TabId;
  onTab: (id: TabId) => void;
  baby: Baby;
  onOpenProfile: () => void;
  children: ReactNode;
}

const TABS: { id: TabId; label: string; key: string; icon: IconName }[] = [
  { id: 'today',      label: 'Today',      key: 'nav.today',      icon: 'today'    },
  { id: 'activities', label: 'Activities', key: 'nav.activities', icon: 'activities' },
  { id: 'list',       label: 'Milestones', key: 'nav.milestones', icon: 'list'     },
  { id: 'journey',    label: 'Journey',    key: 'nav.journey',    icon: 'journey'  },
  { id: 'timeline',   label: 'Timeline',   key: 'nav.timeline',   icon: 'timeline' },
  { id: 'leaps',      label: 'Leaps',      key: 'nav.leaps',      icon: 'leaps'    },
  { id: 'growth',     label: 'Growth',     key: 'nav.growth',     icon: 'growth'   },
  { id: 'ai',         label: 'Ask Alex',   key: 'nav.ai',         icon: 'sparkles' },
];

// The mobile bottom bar drops Timeline, Leaps, and Growth — they are reachable from other screens.
// Desktop keeps them in the sidebar.
const BOTTOM_TABS = TABS.filter((t) => t.id !== 'timeline' && t.id !== 'leaps' && t.id !== 'growth');


/**
 * Responsive app frame.
 *
 *   < nav (768px): bottom tab bar fixed to the viewport, main content scrolls
 *                  full-width with extra bottom padding so it isn't hidden
 *                  behind the bar.
 *
 *   ≥ nav (768px): vertical sidebar (240px) on the left, scrollable main
 *                  column to the right, content centred with max-w-2xl.
 *
 * No JS resize hooks — purely CSS media queries via Tailwind's `nav:` variant
 * (custom breakpoint defined in index.css).
 */
export function AppShell({ tab, onTab, baby, onOpenProfile, children }: AppShellProps) {
  return (
    <div className="min-h-dvh w-full bg-bg text-ink">
      {/* ── Desktop / tablet sidebar ──────────────────────────────────── */}
      <Sidebar tab={tab} onTab={onTab} baby={baby} onOpenProfile={onOpenProfile} />

      {/* ── Main scrollable area ──────────────────────────────────────── */}
      <main
        className="
          min-h-dvh w-full
          pb-[calc(env(safe-area-inset-bottom)+88px)]
          nav:pb-0 nav:pl-[240px]
        "
      >
        <div className="mx-auto w-full max-w-2xl px-5 nav:px-8 pt-4 nav:pt-10">
          {children}
        </div>
      </main>

      {/* ── Mobile bottom tab bar ─────────────────────────────────────── */}
      <BottomTabBar tab={tab} onTab={onTab} />
    </div>
  );
}

/* ── Sidebar (≥nav) ──────────────────────────────────────────────────── */

interface SidebarProps {
  tab: TabId;
  onTab: (id: TabId) => void;
  baby: Baby;
  onOpenProfile: () => void;
}

function Sidebar({ tab, onTab, baby, onOpenProfile }: SidebarProps) {
  const tr = useT();
  return (
    <aside
      className="
        hidden nav:flex
        fixed inset-y-0 left-0 z-30
        w-[240px] flex-col
        border-r border-line bg-bg
        px-4 py-6
      "
    >
      {/* Brand */}
      <div className="px-2 pb-6">
        <h1 className="font-serif text-2xl leading-none tracking-tight text-ink">
          BabyNook
        </h1>
        <p className="mt-1 text-xs text-ink-3 tracking-wide uppercase font-semibold">
          {baby.name} · {baby.ageWeeks} {tr('common.weeksShort', 'wks')}
        </p>
      </div>

      {/* Nav links */}
      <nav className="flex flex-col gap-1">
        {TABS.map((t) => {
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => onTab(t.id)}
              className={[
                'flex items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors',
                active
                  ? 'bg-[color-mix(in_oklch,var(--color-accent)_14%,var(--color-bg))] text-accent-ink font-semibold'
                  : 'text-ink-2 hover:bg-grey-soft hover:text-ink',
              ].join(' ')}
            >
              <Icon name={t.icon} size={20} active={active} />
              <span className="text-sm">{tr(t.key, t.label)}</span>
            </button>
          );
        })}
      </nav>

      <div className="flex-1" />

      {/* Profile button at the bottom */}
      <button
        onClick={onOpenProfile}
        aria-label="Open profile"
        className="flex items-center gap-3 rounded-xl px-2 py-2 text-left hover:bg-grey-soft transition-colors"
      >
        <Avatar size={36} baby={baby} />
        <span className="flex-1 min-w-0">
          <span className="block text-sm font-semibold text-ink leading-tight truncate">
            {baby.name}
          </span>
          <span className="block text-xs text-ink-3 truncate">{tr('nav.viewProfile', 'View profile')}</span>
        </span>
      </button>
    </aside>
  );
}

/* ── Bottom tab bar (<nav) ───────────────────────────────────────────── */

interface BottomTabBarProps {
  tab: TabId;
  onTab: (id: TabId) => void;
}

function BottomTabBar({ tab, onTab }: BottomTabBarProps) {
  const tr = useT();
  return (
    <nav
      className="
        nav:hidden
        fixed inset-x-0 bottom-0 z-40
        bg-bg/95 backdrop-blur
        border-t border-line
        pt-2 px-2
        pb-[calc(env(safe-area-inset-bottom)+8px)]
        flex justify-around
      "
      aria-label="Primary"
    >
      {BOTTOM_TABS.map((t) => {
        const active = tab === t.id;
        return (
          <button
            key={t.id}
            onClick={() => onTab(t.id)}
            className={[
              'flex flex-1 min-w-0 flex-col items-center gap-1 py-1.5 px-2',
              'min-h-11 transition-colors',
              active ? 'text-accent' : 'text-ink-2',
            ].join(' ')}
            aria-current={active ? 'page' : undefined}
          >
            <Icon name={t.icon} size={22} active={active} />
            <span
              className={[
                'text-[10px] tracking-[0.05em] whitespace-nowrap',
                active ? 'font-semibold' : 'font-medium',
              ].join(' ')}
            >
              {tr(t.key, t.label)}
            </span>
          </button>
        );
      })}
    </nav>
  );
}
